import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import PartnerCard from "@/components/landing/PartnerCard";
import { LEADERSHIP } from "@/data/leadership";

/**
 * /leadership — Executive Leadership profiles page.
 *
 * Mirrors the canonical /partnership layout: profiles render through the
 * shared PartnerCard component so the biography, portrait, focus pillars
 * and contact block are visually identical across partnership and
 * leadership tiers.
 *
 * "Leadership" is the executive tier below Partners — senior operational
 * staff who lead a defined function (Capital Formation, Operations, etc.)
 * without partner economics.
 */
const LeadershipPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid="kr-leadership-page"
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Leadership"
          label="Executive Leadership · Function Heads · Institutional Discipline"
          title={
            <>
              The executive layer of a{" "}
              <span className="italic text-[var(--kr-gold)]">
                senior partnership
              </span>
              .
            </>
          }
          description="The Leadership tier comprises senior executives who lead defined functions — capital formation, operations, risk, compliance — supporting the partnership through institutional discipline and specialist depth. Leadership members are not partners and do not hold GP economics; they engage with limited partners and counterparties within their defined function on documented terms."
          breadcrumb="Leadership"
        />

        {/* Leadership profiles — rendered via the same PartnerCard used on /partnership */}
        <section
          data-testid="kr-leadership-profiles"
          className="py-20 lg:py-28"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Leadership
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>

            <div className="space-y-0 border-t border-[var(--kr-line-soft)]">
              {LEADERSHIP.map((m, i) => (
                <PartnerCard key={m.id} partner={m} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing note */}
        <section
          className="py-20 lg:py-24 border-t border-[var(--kr-line-soft)]"
          style={{ background: "var(--kr-bg-2)" }}
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Structure
                  </span>
                  <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.8vw,2.2rem)] leading-[1.2] max-w-[34ch]">
                  Executive leadership supports the{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    partnership
                  </span>{" "}
                  — it does not replace it.
                </h2>
                <p className="mt-6 text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[64ch]">
                  Leadership members lead a defined function and engage with
                  limited partners and counterparties within that function on
                  documented terms. Investment decisions, capital allocation and
                  fiduciary obligations remain with the senior partnership and
                  the Investment Committee.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <Link
                  to="/team"
                  data-testid="kr-leadership-team-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  Return to Team →
                </Link>
                <Link
                  to="/partnership"
                  data-testid="kr-leadership-partnership-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  The Partnership →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default LeadershipPage;
